import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Download, Github, Linkedin, Mail, Search, UserRound } from "lucide-react";
import { APPS } from "@/data/windowRegistry";
import { useWindows } from "@/context/WindowContext";
import { useVisitor } from "@/context/VisitorContext";
import { downloadResume } from "@/utils/download";
import { EmptyState } from "@/components/ui/States";
import { EASE } from "@/components/ui/Reveal";

export const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || "");

export default function Spotlight() {
  const { open } = useWindows();
  const { setPromptOpen } = useVisitor();
  const [visible, setVisible] = useState(false);
  const [query, setQuery] = useState("");
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);

  const items = useMemo(
    () => [
      ...APPS.map((a) => ({ id: `app-${a.id}`, label: `Open ${a.dockLabel}`, hint: "app", Icon: a.icon, run: () => open(a.id), keys: a.dockLabel })),
      {
        id: "act-resume",
        label: "Download resume — AnishResume_WA.pdf",
        hint: "action",
        Icon: Download,
        run: () => {
          downloadResume();
          toast("Resume downloading — AnishResume_WA.pdf");
        },
        keys: "cv pdf download resume",
      },
      { id: "act-github", label: "GitHub — github.com/anishravi", hint: "link", Icon: Github, run: () => window.open("https://github.com/anishravi", "_blank", "noopener"), keys: "code repos profile" },
      { id: "act-linkedin", label: "LinkedIn — linkedin.com/in/anishravi-swe", hint: "link", Icon: Linkedin, run: () => window.open("https://linkedin.com/in/anishravi-swe", "_blank", "noopener"), keys: "profile network" },
      { id: "act-email", label: "Email Anish — anishravi2002@gmail.com", hint: "link", Icon: Mail, run: () => window.open("mailto:anishravi2002@gmail.com"), keys: "contact mail write" },
      { id: "act-hi", label: "Introduce yourself — name greeting", hint: "action", Icon: UserRound, run: () => setPromptOpen(true), keys: "hello name greet hi" },
    ],
    [open, setPromptOpen]
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => `${it.label} ${it.keys || ""}`.toLowerCase().includes(q));
  }, [query, items]);

  const close = () => {
    setVisible(false);
    setQuery("");
    setSel(0);
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setVisible((v) => !v);
        setQuery("");
        setSel(0);
      } else if (e.key === "Escape") {
        setVisible(false);
      }
    };
    const onToggle = () => {
      setVisible((v) => !v);
      setQuery("");
      setSel(0);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("spotlight:toggle", onToggle);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("spotlight:toggle", onToggle);
    };
  }, []);

  useEffect(() => {
    if (visible) {
      const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 80);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [visible]);

  useEffect(() => {
    setSel(0);
  }, [results.length]);

  const run = (it) => {
    close();
    if (it) it.run();
  };

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSel((s) => Math.min(s + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSel((s) => Math.max(s - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      run(results[sel]);
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-[9500] bg-black/45 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
          data-testid="spotlight-overlay"
        >
          <motion.div
            className="glass mx-auto mt-[12vh] w-[min(580px,92vw)] overflow-hidden rounded-2xl"
            initial={{ opacity: 0, scale: 0.96, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: -8, transition: { duration: 0.15 } }}
            transition={{ duration: 0.22, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            data-testid="spotlight"
          >
            <div className="flex items-center gap-3 border-b border-white/10 px-4">
              <Search size={16} className="shrink-0 text-slate-400" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSel(0);
                }}
                onKeyDown={onInputKey}
                data-testid="spotlight-input"
                placeholder="Search — apps, resume, links…"
                aria-label="Spotlight search"
                className="w-full bg-transparent py-3.5 font-mono text-sm text-slate-100 outline-none placeholder:text-slate-600"
              />
              <kbd className="hidden rounded bg-white/10 px-1.5 py-0.5 font-mono text-[10px] text-slate-400 sm:block">{IS_MAC ? "⌘K" : "Ctrl K"}</kbd>
            </div>
            <div className="max-h-[46vh] overflow-y-auto p-2" data-testid="spotlight-results">
              {results.length === 0 ? (
                <EmptyState title={`No results for “${query}”`} hint="try: terminal · skills · resume · github" testid="spotlight-empty" />
              ) : (
                results.map((it, i) => (
                  <button
                    key={it.id}
                    data-testid={`spotlight-result-${i}`}
                    onMouseEnter={() => setSel(i)}
                    onClick={() => run(it)}
                    className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors duration-100 ${i === sel ? "bg-sonoma/20" : ""}`}
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white/10">
                      <it.Icon size={13} className="text-sky-300" />
                    </span>
                    <span className="flex-1 truncate font-mono text-xs text-slate-200">{it.label}</span>
                    <span className="font-mono text-[10px] text-slate-500">{it.hint}</span>
                  </button>
                ))
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
